import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import Cookies from "js-cookie"

import useSlugExtractor from "@hooks/useSlugExtractor"

import getApiErrorMessages from "@utils/getApiErrorMessages"
import replaceSlugs from "@utils/replaceSlugs"

import toast from "@molecules/Toast/Toast"

import { ECOMMERCE_ENDPOINTS } from "@endpoints/ecommerce"

import {
    EcommerceApiErrorResponse,
    EcommerceAuthorizeResponse,
    EcommerceConnectPayload,
    EcommerceWebhookRegistrationSummary,
} from "./SettingsEcommerce.types"
import { getMissingEcommerceScopes } from "./SettingsEcommerce.utils"

async function fetchEcommerceJSON<ResponseData>(endpoint: string): Promise<ResponseData> {
    const response = await fetch(endpoint)

    if (!response.ok) {
        throw new Error(`Request to ${endpoint} failed with status ${response.status}.`)
    }

    return (await response.json()) as ResponseData
}

export function useEcommercePlatformsQuery() {
    const [platformsEndpoint] = useSlugExtractor([ECOMMERCE_ENDPOINTS.PLATFORMS])

    return useQuery({
        queryKey: ["ecommerce-platforms", platformsEndpoint],
        queryFn: () => fetchEcommerceJSON<EcommercePlatformDetails[]>(platformsEndpoint),
    })
}

export function useEcommerceConnectionsQuery() {
    const [connectionsEndpoint] = useSlugExtractor([ECOMMERCE_ENDPOINTS.CONNECTIONS])

    return useQuery({
        queryKey: ["ecommerce-connections", connectionsEndpoint],
        queryFn: () => fetchEcommerceJSON<EcommerceConnection[]>(connectionsEndpoint),
    })
}

export function useInvalidateEcommerceConnections() {
    const queryClient = useQueryClient()
    const [connectionsEndpoint] = useSlugExtractor([ECOMMERCE_ENDPOINTS.CONNECTIONS])

    return () => queryClient.invalidateQueries({ queryKey: ["ecommerce-connections", connectionsEndpoint] })
}

export function useEcommerceAuthorizeMutation() {
    const [authorizeEndpointTemplate] = useSlugExtractor([ECOMMERCE_ENDPOINTS.PLATFORM_AUTHORIZE])

    return useMutation<EcommerceAuthorizeResponse, EcommerceApiErrorResponse, string>({
        mutationFn: async (platform: string) => {
            const response = await fetch(replaceSlugs(authorizeEndpointTemplate, { platform }))

            if (!response.ok) {
                throw (await response.json()) as EcommerceApiErrorResponse
            }

            return (await response.json()) as EcommerceAuthorizeResponse
        },
        onSuccess: (data) => {
            window.location.assign(data.url)
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not start the store authorization." })
        },
    })
}

export function useEcommerceTestConnectionMutation() {
    const [testEndpointTemplate] = useSlugExtractor([ECOMMERCE_ENDPOINTS.CONNECTION_TEST])
    const invalidateConnections = useInvalidateEcommerceConnections()

    return useMutation<EcommerceConnection, Error, EcommerceConnection>({
        mutationFn: async (connection) => {
            const response = await fetch(replaceSlugs(testEndpointTemplate, { id: connection.id }), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
            })

            if (!response.ok) {
                throw new Error(`The connection test request failed with status ${response.status}.`)
            }

            return (await response.json()) as EcommerceConnection
        },
        onSuccess: (connection) => {
            void invalidateConnections()

            if (connection.has_connection_error) {
                toast({
                    type: "error",
                    variant: "detailed",
                    title: `${connection.store_name} has a connection problem`,
                    supportText: connection.connection_error_details.message,
                })
            } else {
                toast({ type: "success", title: `${connection.store_name} is connected` })
            }
        },
        onError: () => {
            toast({ type: "error", title: "Could not test the connection" })
        },
    })
}

export function useEcommerceDisconnectMutation() {
    const [connectionDetailsEndpointTemplate] = useSlugExtractor([ECOMMERCE_ENDPOINTS.CONNECTION_DETAILS])
    const invalidateConnections = useInvalidateEcommerceConnections()

    return useMutation<void, EcommerceApiErrorResponse, EcommerceConnection>({
        mutationFn: async (connection) => {
            const response = await fetch(replaceSlugs(connectionDetailsEndpointTemplate, { id: connection.id }), {
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
            })

            if (!response.ok) {
                throw (await response.json()) as EcommerceApiErrorResponse
            }
        },
        onSuccess: (data, connection) => {
            void invalidateConnections()
            toast({ type: "success", title: `${connection.store_name} disconnected` })
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not disconnect the store" })
        },
    })
}

export function useEcommerceRegisterWebhooksMutation() {
    const [registerWebhooksEndpointTemplate] = useSlugExtractor([ECOMMERCE_ENDPOINTS.CONNECTION_REGISTER_WEBHOOKS])

    return useMutation<EcommerceWebhookRegistrationSummary, EcommerceApiErrorResponse, EcommerceConnection>({
        mutationFn: async (connection) => {
            const response = await fetch(replaceSlugs(registerWebhooksEndpointTemplate, { id: connection.id }), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
            })

            if (!response.ok) {
                throw (await response.json()) as EcommerceApiErrorResponse
            }

            return (await response.json()) as EcommerceWebhookRegistrationSummary
        },
        onSuccess: (summary, connection) => {
            toast({
                type: "success",
                variant: "detailed",
                title: `Webhooks registered for ${connection.store_name}`,
                supportText: `${summary.created.length} newly registered, ${summary.existing.length} already in place.`,
            })
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not register webhooks" })
        },
    })
}

export function useEcommerceCheckScopesMutation() {
    const [checkScopesEndpointTemplate] = useSlugExtractor([ECOMMERCE_ENDPOINTS.CONNECTION_CHECK_SCOPES])
    const invalidateConnections = useInvalidateEcommerceConnections()

    return useMutation<EcommerceConnection, EcommerceApiErrorResponse, EcommerceConnection>({
        mutationFn: async (connection) => {
            const response = await fetch(replaceSlugs(checkScopesEndpointTemplate, { id: connection.id }), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
            })

            if (!response.ok) {
                throw (await response.json()) as EcommerceApiErrorResponse
            }

            return (await response.json()) as EcommerceConnection
        },
        onSuccess: (connection) => {
            void invalidateConnections()

            const missingScopes = getMissingEcommerceScopes(connection)
            if (missingScopes.length > 0) {
                toast({
                    type: "warning",
                    title: `Missing permissions: ${missingScopes.map((scope) => scope.short).join(", ")}.`,
                })
            } else {
                toast({ type: "success", title: "All required permissions present." })
            }
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not check permissions" })
        },
    })
}

export async function connectEcommerceStoreRequest(
    endpoint: string,
    method: "POST" | "PATCH",
    payload: EcommerceConnectPayload,
): Promise<EcommerceConnection> {
    const response = await fetch(endpoint, {
        method,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRFToken": Cookies.get("csrftoken") ?? "",
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        throw (await response.json()) as EcommerceApiErrorResponse
    }

    return (await response.json()) as EcommerceConnection
}
